import type { z } from 'zod';
import type { IValidatedResponse } from '../types';
import { APIClientError } from './api-client-error';
import { CloudflareError } from './cloudflare-error';
import { isHtmlResponse } from './is-html-response';
import { makeCheckedRequest } from './make-checked-request';
import type { IMakeRawRequestParams } from './make-raw-request';
import { ValidationError } from './validation-error';

export interface IMakeValidatedRequestParams<T> extends IMakeRawRequestParams {
  schema?: z.ZodSchema<T>;
}

export async function makeValidatedRequest<T>({
  method,
  path,
  body,
  schema,
  options = {},
}: IMakeValidatedRequestParams<T>): Promise<IValidatedResponse<T>> {
  const response = await makeCheckedRequest({ method, path, body, options });

  let responseData: unknown;
  try {
    responseData = await response.json();
  } catch {
    if (isHtmlResponse(response)) {
      throw new CloudflareError({ status: response.status, path });
    }
    throw new APIClientError(
      'Failed to parse response as JSON',
      response.status,
      response.statusText,
    );
  }

  if (!schema) {
    return {
      success: true,
      data: responseData as T,
      status: response.status,
      statusText: response.statusText,
    };
  }

  const validationResult = schema.safeParse(responseData);
  if (!validationResult.success) {
    const issues = validationResult.error.issues
      .map((i) => `${i.path.join('.')}: ${i.message}`)
      .join('; ');
    throw new ValidationError(
      `Response validation failed: ${issues}`,
      validationResult.error,
      path,
    );
  }

  return {
    success: true,
    data: validationResult.data,
    status: response.status,
    statusText: response.statusText,
  };
}
